import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import DataConnector from 'connect/DataConnector';
import {Strand} from 'constants/MolbioConstants';
import {MatchSite} from 'constants/SequenceConstants';
import EnzymeConstants, {Enzyme} from 'molbio/enzymes/EnzymeConstants';
import * as sequtil from 'molbio/SeqUtil';
import {borderStyle} from 'molbio/SharedStyles';
import preferences from 'Preferences';
import {LEGACY_FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import ErrorBanner from 'ui-library/banners/ErrorBanner';
import LegacyFieldError from 'ui-library/bs/LegacyFieldError';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import {Tab, TabContainer} from 'ui-library/TabContainer';
import sortedBy from 'util/sortedBy';
import {InputChangeEvent} from 'util/Types';

import {
  CodonOptimizationPreferences,
  CodonOptimizationSelectionTab,
  GCLevel,
  HairpinParameter,
  Organism,
} from './CodonOptimizationConstants';
import CodonOptimizationCutsitesPicker, {EnzymeTableRow} from './CodonOptimizationCutsitesPicker';
import CodonOptimizationReducePatternPicker from './CodonOptimizationReducePatternPicker';
import CodonOptimizationTabParameters from './CodonOptimizationTabParameters';
import CodonOptimizationTabProtectRegionsPicker from './CodonOptimizationTabProtectRegionsPicker';
import * as CodonOptimizationTabReduxActions from './CodonOptimizationTabReduxActions';
import CodonOptimizationTabSelectionHeader from './CodonOptimizationTabSelectionHeader';
import {checkInvalidPreviewRequest, checkInvalidProtectedRegion} from './CodonOptimizationUtil';
import {CodonOptimizationTabsSelector} from './reduceAllCodonOptimizationTabs';
import {EnzymeRecognitionSite, PatternToReduce, ProtectedRegion} from './reduceCodonOptimizationTab';

class CodonOptimizationTabSelectionConnector extends DataConnector<
  CodonOptimizationTabSelectionProps,
  EnhancedProps
> {
  enhanceProps(): EnhancedProps {
    const selector = new CodonOptimizationTabsSelector(this.reduxState());
    const state = selector.getState(this.props.tabId);
    assert(
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      state.optimizationStart !== null && state.optimizationEnd !== null,
      'Expected optimizationStart and optimizationEnd to be set.'
    );
    const optimizationStart = state.optimizationStart;
    const optimizationEnd = state.optimizationEnd;
    return {
      optimizationStart,
      optimizationEnd,
      selectedOrganism: state.selectedOrganism,
      selectedGCLevel: state.selectedGCLevel,
      isLoading: state.isLoading,
      error: state.error,
      bases: state.bases,
      cutsitesToPreserve: state.cutsitesToPreserve,
      onPreview: (): void => {
        this.dispatch(
          CodonOptimizationTabReduxActions.onPreview(
            this.props.tabId,
            this.props.sequenceId,
            this.props.isBackTranslation,
            this.props.matchSites,
            state.cutsitesToPreserve,
            state.cutsitesToAvoid,
            optimizationStart,
            optimizationEnd,
            this.props.seqLength,
            this.props.strand,
            state.protectedRegions,
            state.bases,
            state.selectedOrganism,
            state.selectedGCLevel,
            state.uridineDepletion,
            state.avoidHairpins,
            state.hairpinStem,
            state.hairpinWindow,
            state.patternsToReduce
          )
        );
      },
      onUpdateOrganism: (value: Organism): void => {
        preferences.set(CodonOptimizationPreferences.ORGANISM, value);
        this.dispatch(CodonOptimizationTabReduxActions.updateOrganism(value, this.props.tabId));
      },
      onUpdateGCLevel: (value: GCLevel): void => {
        preferences.set(CodonOptimizationPreferences.GC_LEVEL, value);
        this.dispatch(CodonOptimizationTabReduxActions.updateGC(value, this.props.tabId));
      },
      protectedRegions: state.protectedRegions,
      deleteProtectedRegion: (region: ProtectedRegion): void => {
        this.dispatch(CodonOptimizationTabReduxActions.deleteProtectedRegion(this.props.tabId, region));
      },
      deleteAllProtectedRegions: (): void => {
        this.dispatch(CodonOptimizationTabReduxActions.deleteAllProtectedRegions(this.props.tabId));
      },
      addProtectedRegion: (): void => {
        // selection endpoints are 0-indexed, end-exlusive
        // start and end are initialized to match the endpoints in the status bar
        const [start, end] = sequtil.modelRangeToDisplay(
          this.props.currentSelection.start,
          this.props.currentSelection.end,
          this.props.seqLength,
          this.props.startIndex
        );
        const invalidProtectedRegionMessage = checkInvalidProtectedRegion(
          this.props.currentSelection.start,
          this.props.currentSelection.end,
          optimizationStart,
          optimizationEnd,
          state.protectedRegions
        );
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        if (invalidProtectedRegionMessage !== null) {
          this.dispatch(
            CodonOptimizationTabReduxActions.isInvalidProtectedRegion(
              this.props.tabId,
              invalidProtectedRegionMessage
            )
          );
        } else {
          this.dispatch(
            CodonOptimizationTabReduxActions.addProtectedRegion(this.props.tabId, {
              start: this.props.currentSelection.start,
              // If selection is invalid (e.g. end is null) it will be handled in the previous conditional.
              end: this.props.currentSelection.end!,
              name: `${start}-${end}`,
            })
          );
        }
      },
      cutsitesToAvoid: state.cutsitesToAvoid,
      deleteCutsiteToPreserve: (enzymeId: number): void => {
        this.dispatch(CodonOptimizationTabReduxActions.deleteCutsiteToPreserve(this.props.tabId, enzymeId));
      },
      deleteAllCutsitesToPreserve: (): void => {
        this.dispatch(CodonOptimizationTabReduxActions.deleteAllCutsitesToPreserve(this.props.tabId));
      },
      addCutsiteToPreserve: (enzymeId: number): void => {
        const enzyme = EnzymeConstants.enzymes.filter(
          (currentEnzyme: Enzyme) => currentEnzyme.id === enzymeId
        )[0];
        this.dispatch(
          CodonOptimizationTabReduxActions.addCutsiteToPreserve(this.props.tabId, {
            enzymeName: enzyme.name,
            id: enzymeId,
            recognitionSite: enzyme.seq,
          })
        );
      },
      addCutsiteToAvoid: (enzymeId: number) => {
        const cutsite = EnzymeConstants.enzymes.filter((enzyme: Enzyme) => enzyme.id === enzymeId)[0];
        this.dispatch(
          CodonOptimizationTabReduxActions.addCutsiteToAvoid(this.props.tabId, {
            enzymeName: cutsite.name,
            recognitionSite: cutsite.seq,
            id: enzymeId,
          })
        );
      },
      deleteCutsiteToAvoid: (enzymeId: number) => {
        this.dispatch(CodonOptimizationTabReduxActions.deleteCutsiteToAvoid(this.props.tabId, enzymeId));
      },
      deleteAllCutsitesToAvoid: (): void => {
        this.dispatch(CodonOptimizationTabReduxActions.deleteAllCutsitesToAvoid(this.props.tabId));
      },
      uridineDepletion: state.uridineDepletion,
      onUpdateUridineDepletion: (e: InputChangeEvent) => {
        preferences.set(CodonOptimizationPreferences.URIDINE, e.target.checked);
        this.dispatch(
          CodonOptimizationTabReduxActions.updateUridineDepletion(this.props.tabId, e.target.checked)
        );
      },
      avoidHairpins: state.avoidHairpins,
      onUpdateAvoidHairpins: (checked: boolean) => {
        this.dispatch(
          CodonOptimizationTabReduxActions.updateHairpinParameters(
            this.props.tabId,
            checked,
            state.hairpinStem.value,
            state.hairpinWindow.value,
            state.bases.length
          )
        );
      },
      hairpinStem: state.hairpinStem,
      onUpdateHairpinStem: (stem: string) => {
        this.dispatch(
          CodonOptimizationTabReduxActions.updateHairpinParameters(
            this.props.tabId,
            state.avoidHairpins,
            Number(stem),
            state.hairpinWindow.value,
            state.bases.length
          )
        );
      },
      hairpinWindow: state.hairpinWindow,
      onUpdateHairpinWindow: (window: string) => {
        this.dispatch(
          CodonOptimizationTabReduxActions.updateHairpinParameters(
            this.props.tabId,
            state.avoidHairpins,
            state.hairpinStem.value,
            Number(window),
            state.bases.length
          )
        );
      },
      activeTab: state.activeTab,
      onUpdateActiveTab: (tab: CodonOptimizationSelectionTab) => {
        this.dispatch(CodonOptimizationTabReduxActions.updateActiveTab(this.props.tabId, tab));
      },
      addPatternToReduce: () => {
        this.dispatch(CodonOptimizationTabReduxActions.addPatternToReduce(this.props.tabId));
      },
      deleteAllPatternsToReduce: () => {
        this.dispatch(CodonOptimizationTabReduxActions.deleteAllPatternsToReduce(this.props.tabId));
      },
      deletePatternToReduce: (patternIdx: number) => {
        this.dispatch(CodonOptimizationTabReduxActions.deletePatternToReduce(this.props.tabId, patternIdx));
      },
      updatePatternToReduce: (patternIdx: number, patternToReduce: PatternToReduce) => {
        this.dispatch(
          CodonOptimizationTabReduxActions.updatePatternToReduce(
            this.props.tabId,
            patternIdx,
            patternToReduce
          )
        );
      },
      patternsToReduce: state.patternsToReduce,
    };
  }
}

interface EnhancedProps {
  optimizationStart: number;
  optimizationEnd: number;
  selectedOrganism: Organism | null;
  selectedGCLevel: GCLevel;
  isLoading: boolean;
  error: string | null;
  onPreview: () => void;
  bases: string;
  onUpdateOrganism: (value: string) => void;
  onUpdateGCLevel: (value: GCLevel) => void;
  protectedRegions: Array<ProtectedRegion>;
  deleteProtectedRegion: (region: ProtectedRegion) => void;
  deleteAllProtectedRegions: () => void;
  addProtectedRegion: () => void;
  cutsitesToAvoid: Array<EnzymeRecognitionSite>;
  cutsitesToPreserve: Array<EnzymeRecognitionSite>;
  deleteCutsiteToPreserve: (enzymeId: number) => void;
  deleteAllCutsitesToPreserve: () => void;
  addCutsiteToPreserve: (enzymeId: number) => void;
  addCutsiteToAvoid: (enzymeId: number) => void;
  deleteCutsiteToAvoid: (enzymeId: number) => void;
  deleteAllCutsitesToAvoid: () => void;
  uridineDepletion: boolean;
  onUpdateUridineDepletion: (e: InputChangeEvent) => void;
  avoidHairpins: boolean;
  onUpdateAvoidHairpins: (checked: boolean) => void;
  hairpinStem: HairpinParameter;
  onUpdateHairpinStem: (stem: string) => void;
  hairpinWindow: HairpinParameter;
  onUpdateHairpinWindow: (window: string) => void;
  activeTab: CodonOptimizationSelectionTab;
  onUpdateActiveTab: (tab: CodonOptimizationSelectionTab) => void;
  patternsToReduce: Array<PatternToReduce>;
  addPatternToReduce: () => void;
  deleteAllPatternsToReduce: () => void;
  deletePatternToReduce: (patternIdx: number) => void;
  updatePatternToReduce: (patternIdx: number, patternToReduce: PatternToReduce) => void;
}

export interface CodonOptimizationTabSelectionProps {
  onClose: () => void;
  tabId: string;
  sequenceId: string;
  startIndex: number;
  onSetSelection: (start: number, end: number) => void;
  currentSelection: {
    start: number;
    end: number | null;
  };
  seqLength: number;
  isBackTranslation: boolean;
  strand: Strand;
  matchSites: Array<MatchSite> | null;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const CodonOptimizationTabSelection = (props: CodonOptimizationTabSelectionProps & EnhancedProps) => {
  const {optimizationStart, optimizationEnd} = props;
  let preserveRows: Array<EnzymeTableRow> = [];
  let matchSites: Array<MatchSite>;
  let avoidRows;
  if (!props.isBackTranslation) {
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    assert(props.matchSites !== null, 'Expected matchSites to be set for codon optimizing a DNA sequence.');
    matchSites = props.matchSites;
    preserveRows = sortedBy(
      props.cutsitesToPreserve.map((cutsite: EnzymeRecognitionSite) => ({
        enzymeId: cutsite.id,
        enzyme: cutsite.enzymeName,
        cuts: matchSites.filter((matchSite: MatchSite) => matchSite.enzymeName === cutsite.enzymeName).length,
      })),
      [
        {accessor: 'cuts', reversed: true},
        {accessor: 'enzyme', reversed: false},
      ]
    );
    avoidRows = sortedBy(
      props.cutsitesToAvoid.map((cutsite: EnzymeRecognitionSite) => ({
        enzymeId: cutsite.id,
        enzyme: cutsite.enzymeName,
        cuts: matchSites.filter((matchSite: MatchSite) => matchSite.enzymeName === cutsite.enzymeName).length,
      })),
      [{accessor: 'cuts', reversed: true}]
    );
  } else {
    avoidRows = sortedBy(
      props.cutsitesToAvoid.map((cutsite: EnzymeRecognitionSite) => ({
        enzymeId: cutsite.id,
        enzyme: cutsite.enzymeName,
      })),
      [{accessor: 'enzyme', reversed: false}]
    );
  }
  const isOriginalSelection =
    optimizationStart === props.currentSelection.start && optimizationEnd === props.currentSelection.end;
  const invalidProtectedRegionMessage = checkInvalidProtectedRegion(
    props.currentSelection.start,
    props.currentSelection.end,
    optimizationStart,
    optimizationEnd,
    props.protectedRegions
  );
  const invalidPreviewRequestMessage = checkInvalidPreviewRequest(
    props.cutsitesToPreserve,
    props.cutsitesToAvoid,
    props.selectedOrganism,
    props.avoidHairpins,
    props.hairpinStem,
    props.hairpinWindow,
    props.uridineDepletion,
    props.selectedGCLevel,
    props.patternsToReduce
  );
  return (
    <div
      className={css(styles.selectionTab)}
      {...getTestProps({component: 'CodonOptimizationTabSelection', element: 'root'})}
    >
      <CodonOptimizationTabSelectionHeader
        optimizationStart={optimizationStart}
        optimizationEnd={optimizationEnd}
        seqLength={props.seqLength}
        startIndex={props.startIndex}
        onSetSelection={props.onSetSelection}
        bases={props.bases}
        strand={props.strand}
        isOriginalSelection={isOriginalSelection}
        isBackTranslation={props.isBackTranslation}
      />
      <div className={css(styles.body)}>
        <br />
        <CodonOptimizationTabParameters
          selectedOrganism={props.selectedOrganism}
          selectedGCLevel={props.selectedGCLevel}
          onUpdateOrganism={props.onUpdateOrganism}
          onUpdateGCLevel={props.onUpdateGCLevel}
          uridineDepletion={props.uridineDepletion}
          onUpdateUridineDepletion={props.onUpdateUridineDepletion}
          avoidHairpins={props.avoidHairpins}
          onUpdateAvoidHairpins={props.onUpdateAvoidHairpins}
          hairpinStem={props.hairpinStem}
          onUpdateHairpinStem={props.onUpdateHairpinStem}
          hairpinWindow={props.hairpinWindow}
          onUpdateHairpinWindow={props.onUpdateHairpinWindow}
          bases={props.bases}
        />
        <br />
        <TabContainer activeTab={props.activeTab} onRequestTabChange={props.onUpdateActiveTab}>
          <Tab
            tabKey={CodonOptimizationSelectionTab.AVOIDED_CUTSITES}
            title={`Avoided Cut Sites (${avoidRows.length})`}
          >
            <p className={css(styles.explanationText)}>
              For the specified enzymes, existing cut sites will be removed and no new cut sites will be
              created.
            </p>
            <br />
            <CodonOptimizationCutsitesPicker
              rows={avoidRows}
              isShowingCuts={!props.isBackTranslation}
              onAdd={props.addCutsiteToAvoid}
              onDelete={props.deleteCutsiteToAvoid}
              onDeleteAll={props.deleteAllCutsitesToAvoid}
              buttonText="Add cut site to avoid"
              emptyTableText="Select an enzyme below to avoid creating its recognition site in the optimized sequence."
              availableEnzymes={EnzymeConstants.enzymes}
            />
          </Tab>
          {!props.isBackTranslation && (
            <Tab
              tabKey={CodonOptimizationSelectionTab.PRESERVED_CUTSITES}
              title={`Preserved Cut Sites (${preserveRows.length})`}
            >
              <p className={css(styles.explanationText)}>
                Existing cut sites will be protected. No new cut sites will be created.
              </p>
              <br />
              <CodonOptimizationCutsitesPicker
                rows={preserveRows}
                isShowingCuts={true}
                onAdd={props.addCutsiteToPreserve}
                onDelete={props.deleteCutsiteToPreserve}
                onDeleteAll={props.deleteAllCutsitesToPreserve}
                buttonText="Add cut site to preserve"
                emptyTableText="Select an enzyme below to preserve its cut sites."
                availableEnzymes={EnzymeConstants.enzymes.filter((enzyme: Enzyme) =>
                  matchSites.some((matchSite) => matchSite.enzymeId === enzyme.id)
                )}
              />
            </Tab>
          )}
          {!props.isBackTranslation && (
            <Tab
              tabKey={CodonOptimizationSelectionTab.PROTECTED_REGIONS}
              title={`Protected Regions (${props.protectedRegions.length})`}
            >
              <p className={css(styles.explanationText)}>
                Selected regions will not be modified by the optimization routine.
              </p>
              <br />
              <CodonOptimizationTabProtectRegionsPicker
                protectedRegions={props.protectedRegions}
                onDelete={(region: ProtectedRegion) => {
                  props.deleteProtectedRegion(region);
                }}
                onDeleteAll={props.deleteAllProtectedRegions}
                onAdd={props.addProtectedRegion}
                seqLength={props.seqLength}
                startIndex={props.startIndex}
                invalidProtectedRegionMessage={invalidProtectedRegionMessage}
                onSetSelection={props.onSetSelection}
              />
            </Tab>
          )}
          <Tab
            tabKey={CodonOptimizationSelectionTab.REDUCED_PATTERNS}
            title={`Patterns to Reduce (${props.patternsToReduce.length})`}
          >
            <p className={css(styles.explanationText)}>
              {`${
                props.isBackTranslation
                  ? 'The optimization procedure will attempt to minimize the usage of the specified nucleotide pattern(s) throughout the back-translated DNA sequence, on the coding strand only.'
                  : 'The optimization procedure will attempt to reduce the usage of the specified nucleotide pattern(s) throughout the entire sequence length, on the coding strand only.'
              } Use the checkbox if you wish to also reduce usage of the reverse complement. The pattern must consist of ATGC bases only, and is case-insensitive ("gat" will reduce usage of "GAT").`}
            </p>
            <br />
            <CodonOptimizationReducePatternPicker
              rows={props.patternsToReduce}
              onAdd={props.addPatternToReduce}
              onDeleteAll={props.deleteAllPatternsToReduce}
              onDelete={props.deletePatternToReduce}
              onUpdate={props.updatePatternToReduce}
            />
          </Tab>
        </TabContainer>
      </div>
      {
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        props.error !== null && (
          <div className={css(styles.error)}>
            <ErrorBanner>
              <LegacyFieldError message={props.error} />
            </ErrorBanner>
          </div>
        )
      }
      <div className={css(styles.navigationButtons)}>
        <Button
          onClick={props.onClose}
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          size={ButtonSize.MEDIUM}
          text="Cancel"
          className={css(styles.cancelButton)}
        />
        <Tooltip
          // eslint-disable-next-line benchlint/no-eqeqeq-null
          title={invalidPreviewRequestMessage !== null ? invalidPreviewRequestMessage : ''}
          // eslint-disable-next-line benchlint/no-eqeqeq-null
          disabled={invalidPreviewRequestMessage === null}
        >
          <Button
            onClick={props.onPreview}
            buttonStyle={ButtonStyle.SECONDARY}
            size={ButtonSize.MEDIUM}
            text={props.isLoading ? 'Optimizing...' : 'Preview optimization'}
            className={css(styles.previewButton)}
            // eslint-disable-next-line benchlint/no-eqeqeq-null
            disabled={invalidPreviewRequestMessage !== null}
            dataTestAttrs={{component: 'CodonOptimizationTabSelection', element: 'previewButton'}}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default CodonOptimizationTabSelectionConnector.connect(CodonOptimizationTabSelection);

const styles = StyleSheet.create({
  body: {
    padding: '10px 0',
    overflowY: 'auto',
  },
  cancelButton: {
    float: 'left',
  },
  previewButton: {
    float: 'right',
  },
  tableTitle: {
    borderBottom: borderStyle,
    ...LEGACY_FONT_PROXIMA_TITLE,
  },
  tooltip: {
    margin: 6,
  },
  explanationText: {
    color: GRAY_DARKER,
  },
  selectionTab: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: 10,
  },
  title: {
    paddingBottom: 8,
  },
  navigationButtons: {
    borderTop: borderStyle,
    height: 'inherit',
    flexBasis: 0,
    paddingTop: 20,
    position: 'relative',
    marginTop: 'auto',
  },
  error: {
    position: 'relative',
    marginTop: 'auto',
  },
});
